/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { getSpaceUsersList } from "@Services/dataManager";
import { useTranslation } from "@i18n";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { typedColumns, type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import IconComponent from "@CubeComponents/icons";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import { useParams } from "react-router-dom";
import { defaultModulesMapper } from "@Helpers/constants";

interface SelectedUsers {
  id: string;
  spaceId: string;
  spaceName: string;
}

interface Space {
  _id: string;
  description: string;
  spaceName: string;
  createdDate: number;
  status: string;
  id: string;
  createdBy: string;
  spaceId: string;
  projectId: string;
  createdByName: string;
}

type SelectedSpace = Pick<Space, "_id" | "spaceName">;

interface SpaceUserSelecterProps {
  spaceList: Space[];
  onSpaceSelectorModalClose: () => void;
  onConfirm: (users: SelectedUsers[]) => void;
  selectedUsers: SelectedUsers[];
}

interface QueryParams {
  spaceId: string;
  params: {
    spaceId: string;
  };
}

interface SpaceUserRow extends GridRowBase {
  userId: string;
  userName: string;
  email?: string;
  organisation?: string;
  designation?: string;
  spaceId: string;
  spaceName: string;
}

type SlickGridHandle = {
  slickGrid?: {
    setSelectedRows: (rows: number[]) => void;
    getSelectedRows: () => number[];
  };
  dataView?: {
    getAllSelectedItems: () => SpaceUserRow[];
  };
};

function SpaceUserSelector({
  spaceList,
  onSpaceSelectorModalClose,
  onConfirm,
  selectedUsers = [],
}: SpaceUserSelecterProps) {
  const { t } = useTranslation();
  // useRef Declarations
  const spaceUsersRef = useRef<SlickGridHandle | null>(null);

  // State Declarations
  const [selectedSpace, setSelectedSpace] = useState<SelectedSpace | null>(null);
  const [selectedUsersList, setSelectedUsersList] = useState<SelectedUsers[]>(selectedUsers);
  const [isAddToListButtonDisabled, setIsAddToListButtonDisabled] = useState<boolean>(true);
  const [spaceUsersQuery, setSpaceUsersQuery] = useState<QueryParams | null>(null);
  const disabledUserIds = useMemo(() => selectedUsersList.map((user: any) => user.id), [selectedUsersList]);
  const hasAddedNewUsers = useMemo(() => {
    if (selectedUsersList.length <= selectedUsers.length) {
      return false;
    }

    const originalIds = new Set(selectedUsers.map((user: any) => user.id));
    const hasNewUser = selectedUsersList.some((user: any) => !originalIds.has(user.id));

    return hasNewUser;
  }, [selectedUsersList, selectedUsers]);
  const params = useParams();
  const projectId = params.projectId;
  // useEffect to set default space when the Component mounts and set query to fetch users of the space
  useEffect(() => {
    if (spaceList?.length > 0) {
      const defaultSpace = spaceList[0];
      if (!defaultSpace) return;
      setSpaceUsersQuery({
        spaceId: defaultSpace._id,
        params: {
          spaceId: defaultSpace._id,
        },
      });
      setSelectedSpace({
        _id: defaultSpace._id,
        spaceName: defaultSpace.spaceName,
      });
    }
  }, [spaceList]);

  // Processing spaceUsers before passing to the Grid
  const processResponseFunction = (args: { data: { result: SpaceUserRow[] } }) => {
    const processedData = args.data.result.map((user: any) => ({
      id: `${user.id}_${user.spaceId}`,
      userId: user.userId,
      userName: user.userName,
      email: user.email,
      organisation: user.organisation,
      designation: user.designation,
      spaceId: user.spaceId,
      spaceName: user.spaceName,
    }));
    return processedData;
  };

  // Data Manager for Space Users

  // Function to set the selected space and related users in the Grid
  const changeSelectedSpace = (event: { value?: string }) => {
    if (!event.value) return;

    const dropdownSelectedSpace = spaceList.find((space: any) => space._id === event.value);

    if (dropdownSelectedSpace) {
      setSpaceUsersQuery({
        spaceId: dropdownSelectedSpace._id,
        params: {
          spaceId: dropdownSelectedSpace._id,
        },
      });
      setSelectedSpace({
        _id: dropdownSelectedSpace._id,
        spaceName: dropdownSelectedSpace.spaceName,
      });
      setTimeout(() => {
        if (spaceUsersRef.current && spaceUsersRef.current.slickGrid) {
          spaceUsersRef.current.slickGrid.setSelectedRows([]);
          setIsAddToListButtonDisabled(true);
        }
      }, 0);
    }
  };

  // Handler function to exit the modal when user clicks outside the modal
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onSpaceSelectorModalClose();
      }
    },
    [onSpaceSelectorModalClose],
  );

  // Handler function to add selected users to the list
  const handleAddToList = useCallback(() => {
    const selectedItems = spaceUsersRef.current?.dataView?.getAllSelectedItems() ?? [];
    setSelectedUsersList((prevList) => {
      const existingIds = new Set(prevList.map((item: any) => item.id));
      const newUsers = selectedItems
        .filter((item: any) => !existingIds.has(item.id))
        .map((item: any) => ({
          id: item.id,
          spaceId: item.spaceId,
          spaceName: item.spaceName,
        }));
      return [...prevList, ...newUsers];
    });
    if (spaceUsersRef.current && spaceUsersRef.current.slickGrid) {
      spaceUsersRef.current.slickGrid.setSelectedRows([]);
      setIsAddToListButtonDisabled(true);
    }
  }, []);

  // Handler function to select a row in the grid
  const handleRowSelection = () => {
    const selectedRows = spaceUsersRef.current?.slickGrid?.getSelectedRows() ?? [];
    setIsAddToListButtonDisabled(selectedRows.length === 0);
  };

  // Handler function to remove users from the list
  const handleRemoveUser = useCallback((id: string) => {
    setSelectedUsersList((prev) => {
      const updatedList = prev.filter((user: any) => user.id !== id);
      return updatedList;
    });
  }, []);

  // Handler function to confirm addition of users and close the modal
  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(selectedUsersList);
    }
    onSpaceSelectorModalClose();
  }, [selectedUsersList, onConfirm, onSpaceSelectorModalClose]);

  // Space dropdown list
  const spaceDropDownList = () => (
    <SelectSpaces
      moduleName={defaultModulesMapper.TRANSMITTALS}
      dataSource={spaceList as any}
      disablePermissionCheck={true}
      data-testid="spaceDropdownField"
      projectId={projectId}
      selectedSpaceCallback={(params: any) => {
        changeSelectedSpace({ value: params._id });
      }}
    />
  );

  // Formatter function for User Name column in Grid
  const userNameTagFormatter = (
    _row: number,
    _cell: number,
    value: string | undefined,
    _columnDef: unknown,
    dataContext: SpaceUserRow,
  ) => {
    if (!value) return "";

    const container = document.createElement("div");
    renderUserTag(container, { id: dataContext.userId });

    return container;
  };

  // Function to render selected users for each space
  const selectedUsersChips = useMemo(() => {
    return selectedUsersList
      .filter((user: any) => user.spaceId === selectedSpace?._id)
      .map((user: any) => (
        <UserNameTagComponent
          key={user.id}
          id={user.id.split("_")[0]}
          handleDeleteIconClicked={() => handleRemoveUser(user.id)}
        />
      ));
  }, [selectedUsersList, selectedSpace?._id, handleRemoveUser]);

  // SlickGrid column definitions
  const columnDefinitions = useMemo(
    () =>
      typedColumns<SpaceUserRow>([
        {
          id: "userName",
          name: t("User Name"),
          field: "userName",
          formatter: userNameTagFormatter,
          filterType: "Text",
          _valueGetter: (row: SpaceUserRow) => row.userName,
        },
        {
          id: "email",
          name: t("Email ID"),
          field: "email",
          filterType: "Text",
        },
        {
          id: "organisation",
          name: t("Organisation"),
          field: "organisation",
          filterType: "Text",
        },
        {
          id: "designation",
          name: t("Designation"),
          field: "designation",
          filterType: "Text",
        },
      ]),
    [],
  );

  // JSX for the Component
  const modalHeader = useCallback(
    () => (
      <div className="flex items-center justify-between w-full py-2 px-6 bg-surface_stroke_gradient_color1 rounded-t-lg">
        <p className="text-base font-bold">{t("Add Users")}</p>
        <div className="flex items-center gap-4 ms-auto">
          {spaceDropDownList()}
          <IconComponent
            name="close"
            width="0.9375rem"
            height="0.9375rem"
            onClick={onSpaceSelectorModalClose}
            className="cursor-pointer"
          />
        </div>
      </div>
    ),

    [onSpaceSelectorModalClose, spaceList],
  );

  const modalContent = useCallback(
    () => (
      <div className="bg-white">
        <div className="">
          <div style={{ height: "40vh" }}>
            {spaceUsersQuery && (
              <SlickGrid1
                ref={spaceUsersRef as any}
                columnDefinitions={columnDefinitions as any}
                remoteDataSource={getSpaceUsersList}
                query={spaceUsersQuery as any}
                disabledIds={disabledUserIds}
                onClick={handleRowSelection}
                beforeDataBound={processResponseFunction as any}
                onSelectedRowIdsChanged={handleRowSelection}
                selectCheckBoxOnRowClick
                enableFilteringOption
                enableSortingOption
                disableGridMargin
              />
            )}
          </div>
        </div>
        <div className="flex py-4 px-6 gap-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={handleAddToList}
            disabled={isAddToListButtonDisabled}
          >
            <div className="flex items-center gap-1">
              <IconComponent name="add" width="1rem" height="1rem" />
              {t("Add to list")}
            </div>
          </ButtonComponent>
          {selectedUsersList.length !== 0 && <div className="flex gap-4">{selectedUsersChips}</div>}
        </div>
      </div>
    ),
    [
      columnDefinitions,
      spaceUsersQuery,
      handleAddToList,
      selectedUsersList,
      selectedUsersChips,
      disabledUserIds,
      isAddToListButtonDisabled,
    ],
  );

  const modalFooter = useCallback(
    () => (
      <div className="flex w-full py-4 px-6 bg-surface_white border-t border-surface_stroke_gradient_color1 gap-4 rounded-b-lg">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleConfirm} disabled={!hasAddedNewUsers}>
          {t("Confirm")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={onSpaceSelectorModalClose}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    ),
    [handleConfirm, onSpaceSelectorModalClose, hasAddedNewUsers],
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex flex-col items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="w-[60%] bg-white rounded-lg">
        {modalHeader()}
        {modalContent()}
        {modalFooter()}
      </div>
    </div>
  );
}

export default SpaceUserSelector;
